import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@cotyorg/ccx-molecules/IconButton';
import DragSwipe from '@cotyorg/ccx-utils/js/components/DragSwipe';
import MobileNavigation from '../MobileNavigation';
import MobileNavigationHorizontalList from './MobileNavigationHorizontalList';
import MobileNavigationContainer from '../../containers/MobileNavigationContainer';

// TODO [2] Refactor and extract functions (PageComponent and NavigationContent) in individual files

const onSwipe = ({
  event,
  isCurrentPage,
  nested,
  pageEl,
  goBack,
}) => {
  if (event.direction === 'right' && isCurrentPage && nested) {
    pageEl.classList.remove('mobile-navigation__section--no-transition');
    // eslint-disable-next-line no-param-reassign
    pageEl.style.transform = '';
    goBack();
  }
};

const onDragStart = ({ nested, pageEl }) => {
  if (nested) {
    // cannot add this class with react
    // - doing so causes drag event to stop firing
    pageEl.classList.add('mobile-navigation__section--no-transition');
  }
};

const onDrag = ({
  event,
  isCurrentPage,
  nested,
  pageEl,
}) => {
  if (pageEl && isCurrentPage && nested) {
    const distance = (event.distance <= 0 && event.direction === 'right') ? event.distance : 0;
    // eslint-disable-next-line no-param-reassign
    pageEl.style.transform = `translateX(calc(-100% - ${distance}px))`;
  }
};

const onDragEnd = ({
  event,
  isCurrentPage,
  nested,
  pageEl,
  goBack,
}) => {
  if (nested) {
    pageEl.classList.remove('mobile-navigation__section--no-transition');
    // eslint-disable-next-line no-param-reassign
    pageEl.style.transform = '';

    if (Math.abs(event.distance) > pageEl.offsetWidth / 3 && isCurrentPage) {
      goBack();
    }
  }
};

const PageComponent = ({
  page,
  nested,
  navigation,
  showPage,
  goBack,
  goBackDone,
  leftChevronIconSrc,
  rightChevronIconSrc,
  disableSwipe,
}) => {
  if (!navigation || !Array.isArray(navigation.displayedPages)) {
    return null;
  }

  const isCurrentPage = navigation.displayedPages[navigation.displayedPages.length - 1] === page.slug;
  const isVisible = navigation.displayedPages.includes(page.slug);
  const shouldRenderContent = page.pages && (isVisible || navigation.goingBack);
  const hideAll = !navigation.displayedPages.length && navigation.goingBack && nested;

  const className = classNames(
    'mobile-navigation__section',
    'flex-column', {
      'mobile-navigation__section--nested': nested,
      'mobile-navigation__section--visible': isVisible || hideAll,
    },
  );

  return (
    <NavigationContent
      page={page}
      nested={nested}
      navigation={navigation}
      showPage={showPage}
      goBack={goBack}
      goBackDone={goBackDone}
      leftChevronIconSrc={leftChevronIconSrc}
      rightChevronIconSrc={rightChevronIconSrc}
      isCurrentPage={isCurrentPage}
      shouldRenderContent={shouldRenderContent}
      className={className}
      disableSwipe={disableSwipe}
    />
  );
};

PageComponent.propTypes = {
  page: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  navigation: PropTypes.shape({
    displayedPages: PropTypes.array.isRequired,
    goingBack: PropTypes.bool,
  }),
  nested: PropTypes.bool.isRequired,
  showPage: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goBackDone: PropTypes.func.isRequired,
  leftChevronIconSrc: PropTypes.string,
  rightChevronIconSrc: PropTypes.string,
  disableSwipe: PropTypes.bool,
};

const NavigationContent = ({
  page,
  nested,
  navigation,
  showPage,
  goBack,
  goBackDone,
  leftChevronIconSrc,
  rightChevronIconSrc,
  isCurrentPage,
  shouldRenderContent,
  className,
  disableSwipe = false,
}) => {
  let pageEl;

  if (disableSwipe) {
    return (
      <div
        className={className}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'transform' && navigation.goingBack) {
            goBackDone();
          }
        }}
        ref={(el) => { pageEl = el; }}
      >
        { shouldRenderContent && nested && page && (
          <div role="menuitem">
            <IconButton
              onClick={() => { goBack(); }}
              extraClass="mobile-navigation__back-button"
              iconSrc={leftChevronIconSrc}
            >
              {page.name}
            </IconButton>
          </div>
        )}

        { shouldRenderContent && (
          <MobileNavigationHorizontalList
            page={page}
            nested={nested}
            navigation={navigation}
            showPage={showPage}
            goBack={goBack}
            goBackDone={goBackDone}
            PageComponent={PageComponent}
            leftChevronIconSrc={leftChevronIconSrc}
            rightChevronIconSrc={rightChevronIconSrc}
            disableSwipe={disableSwipe}
          />
        ) }

      </div>
    );
  }

  return (
    <DragSwipe
      onSwipe={event => onSwipe({
        event,
        isCurrentPage,
        nested,
        pageEl,
        goBack,
      })}
      onDragStart={() => onDragStart({
        nested,
        pageEl,
      })}
      onDrag={event => onDrag({
        event,
        isCurrentPage,
        nested,
        pageEl,
      })}
      onDragEnd={event => onDragEnd({
        event,
        isCurrentPage,
        nested,
        pageEl,
        goBack,
      })}
    >
      <div
        className={className}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'transform' && navigation.goingBack) {
            goBackDone();
          }
        }}
        ref={(el) => { pageEl = el; }}
      >
        { shouldRenderContent && nested && page && (
          <div role="menuitem">
            <IconButton
              onClick={() => { goBack(); }}
              extraClass="mobile-navigation__back-button"
              iconSrc={leftChevronIconSrc}
            >
              {page.name}
            </IconButton>
          </div>
        )}

        { shouldRenderContent && (
          <MobileNavigationHorizontalList
            page={page}
            nested={nested}
            navigation={navigation}
            showPage={showPage}
            goBack={goBack}
            goBackDone={goBackDone}
            PageComponent={PageComponent}
            leftChevronIconSrc={leftChevronIconSrc}
            rightChevronIconSrc={rightChevronIconSrc}
            disableSwipe={disableSwipe}
          />
        ) }

      </div>
    </DragSwipe>
  );
};

NavigationContent.propTypes = {
  page: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  navigation: PropTypes.shape({
    displayedPages: PropTypes.array.isRequired,
    goingBack: PropTypes.bool,
  }),
  nested: PropTypes.bool.isRequired,
  showPage: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goBackDone: PropTypes.func.isRequired,
  leftChevronIconSrc: PropTypes.string,
  rightChevronIconSrc: PropTypes.string,
  disableSwipe: PropTypes.bool,
  isCurrentPage: PropTypes.bool,
  shouldRenderContent: PropTypes.bool,
  className: PropTypes.string,
};

const MobileNavigationHorizontal = ({
  isVisible,
  leftChevronIconSrc,
  rightChevronIconSrc,
  disableSwipe,
}) => (
  <MobileNavigationContainer
    isVisible={isVisible}
    PageComponent={PageComponent}
    childComponent={MobileNavigation}
    leftChevronIconSrc={leftChevronIconSrc}
    rightChevronIconSrc={rightChevronIconSrc}
    disableSwipe={disableSwipe}
  />
);

MobileNavigationHorizontal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  rightChevronIconSrc: PropTypes.string,
  leftChevronIconSrc: PropTypes.string,
  disableSwipe: PropTypes.bool,
};

export default MobileNavigationHorizontal;
